import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Page, Card, Text, Button, Box, Spinner,
  Toast, Modal, EmptyState, Select
} from '@shopify/polaris';
import { DeleteIcon } from '@shopify/polaris-icons';
import { useNavigate, useParams } from 'react-router-dom';
import ProtectionPlanForm, { formToPayload, initialFormState } from '../components/ProtectionPlanForm';
import PreviewPanel from '../components/PreviewPanel';
import SaveBar from '../components/SaveBar';
import { useApi } from '../hooks/useApi';
import { useI18n } from '../i18n';
import { DEFAULT_SETTINGS } from '../constants';

export default function PlanSettings() {
  const { id } = useParams();
  const isNew = id === 'new';
  const navigate = useNavigate();
  const { t } = useI18n();
  const { getPlans, createPlan, updatePlan, deletePlan, getSettings, getLocales, uploadFile } = useApi();

  const [plans, setPlans] = useState([]); // saved list, for preview context
  const [plan, setPlan] = useState(null); // saved version being edited
  const [isActive, setIsActive] = useState(true); // rendered as the status card
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [locales, setLocales] = useState(null); // store languages for the switcher
  const [draft, setDraft] = useState(null); // live form payload for preview
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [toast, setToast] = useState(null);
  const formRef = useRef(null);

  useEffect(() => {
    getSettings().then(setSettings).catch(() => {});
  }, [getSettings]);

  // 店铺语言列表(Shopify published locales):失败时保持 null,表单退回
  // 内置预设语言列表。
  useEffect(() => {
    getLocales()
      .then((data) => setLocales(data.locales || []))
      .catch(() => {});
  }, [getLocales]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setNotFound(false);
    getPlans()
      .then((data) => {
        if (cancelled) return;
        setPlans(data);
        if (isNew) {
          setPlan(null);
          setIsActive(true);
        } else {
          const found = data.find(p => p.id === id);
          if (found) {
            setPlan(found);
            setIsActive(found.isActive);
          } else {
            setNotFound(true);
          }
        }
      })
      .catch(() => { if (!cancelled) setNotFound(true); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [id, isNew, getPlans]);

  // Identity changes when isActive flips, which re-runs the form's draft
  // effect so the preview always reflects the current status.
  const [formState, setFormState] = useState(null); // live form values, for dirty check
  const [formResetKey, setFormResetKey] = useState(0); // bump to remount form = discard
  const handleDraftChange = useCallback((formData) => {
    setFormState(formData);
    setDraft(formToPayload(formData, isActive));
  }, [isActive]);

  // 脏状态比较"提交载荷"而不是原始表单值:formToPayload 做了 trim/数值
  // 规整,和服务端返回值往返一致 —— 保存后两者相同,顶栏立即收起,不会因
  // "2" vs "2.00" 这类表示差异误报未保存;isActive 也一并纳入载荷比较。
  // 新建同样比较:基线是表单的预填默认值 —— 没动过就不弹保存栏,和编辑页
  // 行为一致,而不是一进页面就常驻。
  const savedForm = isNew ? initialFormState(null) : (plan ? initialFormState(plan) : null);
  const formPayload = formState ? formToPayload(formState, isActive) : null;
  const savedPayload = savedForm
    ? formToPayload(savedForm, isNew ? true : plan.isActive)
    : null;
  const dirty = !!formPayload && !!savedPayload
    && JSON.stringify(formPayload) !== JSON.stringify(savedPayload);

  // 离开本页前先收起顶栏:ui-save-bar 渲染在 Shopify admin 顶层(iframe
  // 之外),带着 open=true 卸载本组件时,hide 消息随通道断开丢失,admin 顶
  // 栏会卡在下一个页面上。先渲染一帧收起状态让 hide 送达 admin,再跳转。
  const [barClosing, setBarClosing] = useState(false);
  const leaveVia = useCallback((to, state) => {
    setBarClosing(true);
    setTimeout(() => navigate(to, state ? { state } : undefined), 100);
  }, [navigate]);

  // 放弃改动:已有计划 → 重挂载表单回到已保存值;新建 → 先收顶栏再回列表。
  const handleDiscard = useCallback(() => {
    if (isNew) {
      leaveVia('/plans');
      return;
    }
    setIsActive(plan?.isActive ?? true);
    setFormResetKey(key => key + 1);
  }, [isNew, leaveVia, plan]);

  // Preview list: saved plans with the plan being edited replaced by its live
  // draft (or the new plan appended), so merchants simulate unsaved changes.
  // __preview keeps the edited plan visible even when saved inactive; the
  // separate __dirty flag drives the "Unsaved" badge so it clears on save.
  const previewPlans = useMemo(() => {
    if (isNew) {
      return draft ? [...plans, { ...draft, id: 'draft', __preview: true, __dirty: dirty }] : plans;
    }
    if (!plan || !draft) return plans;
    return plans.map(p => (p.id === plan.id ? { ...plan, ...draft, __preview: true, __dirty: dirty } : p));
  }, [plans, plan, draft, isNew, dirty]);

  // 保存成功提示:嵌入模式调 App Bridge 的 shopify.toast,弹在 Shopify
  // admin 最顶部(iframe 之外,即原生 "Saved successfully" 的位置);
  // 本地开发没有 App Bridge 宿主,退回 Polaris Toast。
  const showSavedToast = useCallback(() => {
    const bridge = window.shopify && window.shopify.toast;
    if (bridge && typeof bridge.show === 'function') {
      bridge.show(t('common.savedToast'));
    } else {
      setToast({ content: t('common.savedToast') });
    }
  }, [t]);

  // 保存后留在当前页(与 Shopify 原生一致):plan 指向已保存记录 → 脏状态
  // 清零 → 顶栏在页面仍挂载时正常收起。保存成功后立刻跳转会"带着可见的
  // 顶栏"卸载组件,hide 消息随通道断开丢失,admin 顶栏就会卡住不消失。
  // 新建例外:保存后 replace 到 /plans/:id —— 路由语义变了(继续停留会
  // 二次保存生成重复计划),此时顶栏已先收起,随后的加载卸载是安全的。
  const handleSave = useCallback(async (payload) => {
    setSaving(true);
    try {
      if (isNew) {
        const created = await createPlan(payload);
        setPlans(prev => [...prev, created]);
        setPlan(created);
        setIsActive(created.isActive ?? true);
        showSavedToast();
        navigate(`/plans/${created.id}`, { replace: true });
      } else {
        const updated = await updatePlan(id, payload);
        setPlans(prev => prev.map(p => (p.id === updated.id ? updated : p)));
        setPlan(updated);
        setIsActive(updated.isActive ?? true);
        showSavedToast();
      }
    } catch (err) {
      setToast({ content: t('plan.saveFailed', { message: err.message }) });
    } finally {
      setSaving(false);
    }
  }, [isNew, id, createPlan, updatePlan, navigate, showSavedToast, t]);

  const handleDelete = useCallback(async () => {
    setSaving(true);
    try {
      await deletePlan(id);
      leaveVia('/plans', { toast: t('plans.deleted') });
    } catch (err) {
      setToast({ content: t('plans.deleteFailed', { message: err.message }) });
      setConfirmDelete(false);
    } finally {
      setSaving(false);
    }
  }, [id, deletePlan, leaveVia, t]);

  if (loading) {
    return (
      <Page backAction={{ onAction: () => navigate('/plans') }} title={t('plan.pageTitle')}>
        <div style={{ padding: '64px', textAlign: 'center' }}>
          <Spinner accessibilityLabel={t('plan.loading')} size="large" />
        </div>
      </Page>
    );
  }

  if (notFound) {
    return (
      <Page backAction={{ onAction: () => navigate('/plans') }} title={t('plan.pageTitle')}>
        <Card>
          <EmptyState
            heading={t('plan.notFound.heading')}
            action={{ content: t('plan.backToPlans'), onAction: () => navigate('/plans') }}
            image="https://cdn.shopify.com/s/files/1/0262/4071/1926/files/emptystate-files.png"
          >
            <p>{t('plan.notFound.body')}</p>
          </EmptyState>
        </Card>
      </Page>
    );
  }

  // backAction 不能给 url:Polaris 会渲染成 <a href>,点击后整页重载 iframe
  // 且丢 ?shop=&host=,重跑整套启动/鉴权流程(Connecting 10s+)。走 onAction
  // + react-router 的 SPA 跳转,并经 leaveVia 先收起保存栏再离开。
  return (
    <Page
      backAction={{ onAction: () => leaveVia('/plans') }}
      title={isNew ? t('plan.newTitle') : (plan?.displayName || plan?.name)}
      subtitle={isNew
        ? t('plan.newSubtitle')
        : t('plan.subtitle')}
      secondaryActions={!isNew ? [
        { content: t('common.delete'), icon: DeleteIcon, destructive: true, onAction: () => setConfirmDelete(true) }
      ] : undefined}
    >
      {/* 窄屏(手机/平板竖屏)退化为单列:表单在前,状态卡+预览整宽垫底,
          预览不再溢出视口(修复手机端右栏把表单挤到 0 宽、总计列被裁掉的
          问题);断点取 900px —— 380px 预览栏 + 可用表单(~440px)并排的
          最小合理宽度,低于它并排只会互相挤压。 */}
      <style>{`
        .pp-plan-layout { display: flex; gap: 20px; align-items: flex-start; }
        .pp-plan-main { flex: 1; min-width: 0; }
        .pp-plan-rail { width: 380px; flex-shrink: 0; position: sticky; top: 16px; }
        @media (max-width: 899px) {
          .pp-plan-layout { flex-direction: column; }
          .pp-plan-rail { width: 100%; position: static; }
        }
      `}</style>
      {/* 有未保存改动时固定在 Shopify admin 最顶部的保存栏(头部不再放保存键);
          barClosing 期间强制收起,保证跳转前 hide 消息已送达 admin 顶层 */}
      <SaveBar
        visible={dirty && !barClosing}
        saving={saving}
        saveLabel={isNew ? t('common.createPlan') : t('common.save')}
        onSave={() => formRef.current?.submit()}
        onDiscard={handleDiscard}
      />
      <div style={{ maxWidth: '1140px', margin: '0 auto' }}>
        <div className="pp-plan-layout">
          {/* Left: sectioned form cards (reference layout) */}
          <div className="pp-plan-main">
            <ProtectionPlanForm
              key={formResetKey}
              ref={formRef}
              plan={plan}
              isActive={isActive}
              onSave={handleSave}
              onDraftChange={handleDraftChange}
              locales={locales}
              uploadFile={uploadFile}
            />
          </div>

          {/* Right rail: plan status card + live preview (reference layout) */}
          <div className="pp-plan-rail">
            <Card>
              <Box padding="400">
                <Text variant="bodyMd" as="p" fontWeight="medium">{t('plan.statusTitle')}</Text>
                <Box paddingBlockStart="200">
                  <Select
                    label=""
                    labelHidden
                    options={[
                      { label: t('plans.statusActive'), value: 'active' },
                      { label: t('plans.statusInactive'), value: 'inactive' }
                    ]}
                    value={isActive ? 'active' : 'inactive'}
                    onChange={(value) => setIsActive(value === 'active')}
                  />
                </Box>
              </Box>
            </Card>

            <Box paddingBlockStart="400">
              <PreviewPanel plans={previewPlans} settings={settings} />
            </Box>
          </div>
        </div>
      </div>

      <Modal
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        title={t('plans.deleteTitle')}
        primaryAction={{
          content: t('common.delete'),
          destructive: true,
          loading: saving,
          onAction: handleDelete
        }}
        secondaryActions={[{ content: t('common.cancel'), onAction: () => setConfirmDelete(false) }]}
      >
        <Modal.Section>
          <Text variant="bodyMd" as="p">
            <strong>{plan?.name}</strong> {t('plans.deleteBody')}
          </Text>
        </Modal.Section>
      </Modal>

      {toast && <Toast content={toast.content} onDismiss={() => setToast(null)} />}
    </Page>
  );
}
